import { KIND_TO_FIELD, type StowedElement } from '@/api/types/stowed-element'
import type {
  GroupElementsRequest,
  UngroupElementDto,
  UngroupElementsRequest,
} from '@/api/types/group-elements-api'
import type { ShapeData } from '@/lib/shapes/types/shape-data'
import type { TextBoxData } from '@/lib/text/types/text-box-data'
import { buildStowedElement } from '@/lib/elements/stowed-element-builder'
import { fractionalIndexMidpoint } from '@/lib/elements/stowed-fractional-index'
import type { DocumentProjection } from '@/lib/canvas/types/document-projection'
import { IDENTITY_PROJECTION } from '@/lib/canvas/document-projection'
import { isGroupShape } from '@/lib/groups/group-kind'
import { computeGroupBoundsFromShapes, textBoxAsObbShape } from '@/lib/groups/shape-group-bounds'
import { isDescendantOfGroup } from '@/lib/groups/shape-group-tree'
import { isTextUnderGroup } from '@/lib/groups/text-under-group'

/**
 * Build POST …/elements/group body. Child geometry stays canvas-absolute on each child row
 * (not included here — only objectId/kindId refs); see `group-elements-api.ts`.
 *
 * `projection` must be the same `spreadProjection` used to persist every other element on this
 * spread (see `document-projection.ts`) — the group row's own `base` goes through
 * `buildStowedElement`/`buildBase` same as any shape, so an identity default here would save the
 * group's center in the wrong frame and reload it shifted by the projection offset (roughly half
 * the spread width) while unaffected sibling elements stay put.
 */
export function buildGroupElementsRequest(
  group: ShapeData,
  childShapesInZOrder: ShapeData[],
  childTextInZOrder?: TextBoxData[],
  projection: DocumentProjection = IDENTITY_PROJECTION
): GroupElementsRequest {
  if (!isGroupShape(group)) {
    throw new Error('buildGroupElementsRequest: group must be kind 7')
  }

  const groupStowed = buildStowedElement(group, undefined, projection)

  if (groupStowed.kindId !== 7 || !groupStowed.groupElement) {
    throw new Error('buildGroupElementsRequest: expected groupElement on stowed group')
  }

  const shapeRefs = childShapesInZOrder.map((child) => {
    const stowed = buildStowedElement(child, undefined, projection)

    return { objectId: child.id, kindId: stowed.kindId }
  })
  const textRefs = (childTextInZOrder ?? []).map((tb) => ({
    objectId: tb.id,
    // Text is persisted as a bare kindId-1 rectangle, not its own top-level kind — match that
    // here even though the backend's /group endpoint doesn't currently read this ref's kindId for
    // anything (see build-group-api-payloads plan notes).
    kindId: 1 as const,
  }))

  return {
    objectId: group.id,
    kindId: 7,
    fractionalIndex: groupStowed.fractionalIndex ?? '',
    groupElement: groupStowed.groupElement,
    elements: [...shapeRefs, ...textRefs],
  }
}

function ungroupDtoFromStowedElement(stowed: StowedElement): UngroupElementDto {
  const fieldName = KIND_TO_FIELD[stowed.kindId]
  const kindElement = fieldName
    ? (stowed[fieldName] as { base?: { x?: number; y?: number } } | undefined)
    : undefined
  const x = kindElement?.base?.x
  const y = kindElement?.base?.y
  const { fractionalIndex } = stowed

  if (
    typeof x !== 'number' ||
    !Number.isFinite(x) ||
    typeof y !== 'number' ||
    !Number.isFinite(y) ||
    typeof fractionalIndex !== 'string' ||
    fractionalIndex.trim() === ''
  ) {
    throw new Error(
      `buildUngroupElementsApiBody: element ${stowed.objectId} has no x/y base or fractionalIndex`
    )
  }

  return {
    objectId: stowed.objectId,
    x,
    y,
    fractionalIndex: fractionalIndex.trim(),
  }
}

/**
 * Build POST …/elements/ungroup/{id} DTOs in stable child-list order.
 *
 * `projection` must match `spreadProjection` so each promoted child's document-relative x/y
 * coordinates are persisted without the canvas projection offset.
 */
export function buildUngroupElementsApiBody(
  childIds: readonly string[],
  shapesInListOrder: ShapeData[],
  textBoxesInListOrder?: TextBoxData[],
  projection: DocumentProjection = IDENTITY_PROJECTION
): UngroupElementsRequest {
  if (childIds.length === 0) return []
  if (new Set(childIds).size !== childIds.length) {
    throw new Error('buildUngroupElementsApiBody: child IDs must be unique')
  }

  const texts = textBoxesInListOrder ?? []
  const out: UngroupElementsRequest = []

  for (const id of childIds) {
    const shape = shapesInListOrder.find((s) => s.id === id)

    if (shape) {
      out.push(ungroupDtoFromStowedElement(buildStowedElement(shape, undefined, projection)))
      continue
    }

    const box = texts.find((t) => t.id === id)

    if (box) {
      out.push(ungroupDtoFromStowedElement(buildStowedElement(box, undefined, projection)))
      continue
    }

    throw new Error(`buildUngroupElementsApiBody: child ${id} was not found`)
  }

  return out
}

function buildSurvivorRestructureRow(
  survivor: ShapeData,
  flatNonGroupMembers: ShapeData[],
  flatTextMembers: TextBoxData[],
  projection: DocumentProjection
): StowedElement[] {
  // Shapes then text, matching buildGroupElementsRequest's [...shapeRefs, ...textRefs] convention
  // for a plain (non-nested) group — keeps the two group-creation wire paths consistent.
  const elementStowed = [
    ...flatNonGroupMembers.map((c) => buildStowedElement(c, undefined, projection)),
    ...flatTextMembers.map((t) => buildStowedElement(t, undefined, projection)),
  ]
  const boundsMembers = [...flatNonGroupMembers, ...flatTextMembers.map(textBoxAsObbShape)]

  if (boundsMembers.length === 0) {
    const survivorStowed = buildStowedElement(survivor, undefined, projection)

    return [{ ...survivorStowed, elements: [] }]
  }

  // Flattening reuses the survivor's own (pre-existing) row instead of giving the wrapper group a
  // row of its own, so the survivor's original fractionalIndex — assigned before this wrap ever
  // happened — would otherwise persist unchanged. Any member added by this wrap (a sibling shape,
  // a nested group's now-flattened content, etc.) can have a fractionalIndex from *after* that
  // point, which would then render above the flattened group's own chrome once reloaded. Re-anchor
  // the survivor's fractionalIndex above every member it now directly owns, mirroring how a fresh
  // (non-nested) group is always appended to the top of unifiedLayerIds at creation time.
  //
  // Must read fractionalIndex from flatNonGroupMembers/flatTextMembers directly, NOT boundsMembers
  // — textBoxAsObbShape (used to fold text into the shape-only bounds calc below) builds a bare
  // synthetic shape and does not carry fractionalIndex over, so text's real value would silently
  // read as '' here. That previously produced a group fractionalIndex computed only from the
  // shape members' max — which can come out *equal to* (not above) a text member's own FI,
  // violating the API's strictly-ascending-fractionalIndex validation and failing the whole
  // restructure call outright (rejecting the shapes along with the text).
  const maxMemberFI = [...flatNonGroupMembers, ...flatTextMembers].reduce(
    (max, m) => ((m.fractionalIndex ?? '') > max ? (m.fractionalIndex ?? '') : max),
    ''
  )
  const survivorFractionalIndex = fractionalIndexMidpoint(maxMemberFI, '')

  const obb = computeGroupBoundsFromShapes(survivor, boundsMembers)

  if (!obb) {
    const survivorStowed = buildStowedElement(
      { ...survivor, fractionalIndex: survivorFractionalIndex },
      undefined,
      projection
    )

    return [{ ...survivorStowed, elements: elementStowed }]
  }

  const eps = 1e-4
  const survivorForStowed: ShapeData = {
    ...survivor,
    x: obb.x,
    y: obb.y,
    width: Math.max(eps, obb.width),
    height: Math.max(eps, obb.height),
    fractionalIndex: survivorFractionalIndex,
  }

  const survivorStowed = buildStowedElement(survivorForStowed, undefined, projection)

  return [
    {
      ...survivorStowed,
      elements: elementStowed,
    },
  ]
}

/**
 * POST …/elements/restructure — one kind-7 row (`survivorGroupId`) with flat `elements` for every
 * non-group descendant (shapes and text) under that group (canvas-absolute). Used after ungroup
 * when persisting each nested child group.
 *
 * `projection` must match `spreadProjection` — see `buildGroupElementsRequest`.
 */
export function buildRestructureBodyAsGroupSurvivor(
  shapesInListOrder: ShapeData[],
  survivorGroupId: string,
  textBoxesInListOrder: TextBoxData[] = [],
  projection: DocumentProjection = IDENTITY_PROJECTION
): StowedElement[] | null {
  const byId = new Map(shapesInListOrder.map((s) => [s.id, s]))
  const survivor = shapesInListOrder.find((s) => s.id === survivorGroupId)

  if (!survivor || !isGroupShape(survivor)) return null

  const flatNonGroup = shapesInListOrder.filter(
    (s) =>
      s.id !== survivorGroupId && isDescendantOfGroup(s, survivorGroupId, byId) && !isGroupShape(s)
  )

  flatNonGroup.sort(
    (a, b) =>
      shapesInListOrder.findIndex((x) => x.id === a.id) -
      shapesInListOrder.findIndex((x) => x.id === b.id)
  )

  const flatText = textBoxesInListOrder.filter((t) => isTextUnderGroup(t, survivorGroupId, byId))

  flatText.sort(
    (a, b) =>
      textBoxesInListOrder.findIndex((x) => x.id === a.id) -
      textBoxesInListOrder.findIndex((x) => x.id === b.id)
  )

  return buildSurvivorRestructureRow(survivor, flatNonGroup, flatText, projection)
}

/**
 * POST …/elements/restructure when the new wrapper has **≥1** direct kind-7 child (group + shapes
 * or multiple groups). Client keeps nested tree; payload picks a **survivor** nested group
 * (earliest in scene list among direct kind-7 children) and a flat `elements` list of every
 * non-group descendant — shapes and text — under the **wrapper** (canvas-absolute).
 *
 * `projection` must match `spreadProjection` — see `buildGroupElementsRequest`.
 */
export function buildRestructureBodyWrappedMultiGroups(
  shapesInListOrder: ShapeData[],
  wrapperGroupId: string,
  textBoxesInListOrder: TextBoxData[] = [],
  projection: DocumentProjection = IDENTITY_PROJECTION
): StowedElement[] | null {
  const byId = new Map(shapesInListOrder.map((s) => [s.id, s]))
  const wrapper = shapesInListOrder.find((s) => s.id === wrapperGroupId)

  if (!wrapper || !isGroupShape(wrapper)) return null

  const directChildren = shapesInListOrder.filter((s) => s.parentGroupId === wrapperGroupId)
  const nestedGroups = directChildren.filter((s) => isGroupShape(s))

  if (nestedGroups.length === 0) return null

  let survivor = nestedGroups[0]
  let bestIdx = Infinity

  for (const g of nestedGroups) {
    const ix = shapesInListOrder.findIndex((s) => s.id === g.id)

    if (ix !== -1 && ix < bestIdx) {
      bestIdx = ix
      survivor = g
    }
  }

  const flatNonGroup: ShapeData[] = []

  for (const s of shapesInListOrder) {
    if (s.id === wrapperGroupId) continue
    if (!isDescendantOfGroup(s, wrapperGroupId, byId)) continue
    if (isGroupShape(s)) continue
    flatNonGroup.push(s)
  }

  flatNonGroup.sort(
    (a, b) =>
      shapesInListOrder.findIndex((x) => x.id === a.id) -
      shapesInListOrder.findIndex((x) => x.id === b.id)
  )

  const flatText = textBoxesInListOrder.filter((t) => isTextUnderGroup(t, wrapperGroupId, byId))

  flatText.sort(
    (a, b) =>
      textBoxesInListOrder.findIndex((x) => x.id === a.id) -
      textBoxesInListOrder.findIndex((x) => x.id === b.id)
  )

  return buildSurvivorRestructureRow(survivor, flatNonGroup, flatText, projection)
}
